import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  useWindowScroll,
  useMedia,
  useLockBodyScroll,
  useWindowSize,
} from "react-use";
import { HeaderProps } from "./types/header.types";
import { useScrollWithOffset } from "../../hooks/use-scroll-offset";
import "./Header.scss";

import HeaderLogo from "./components/HeaderLogo/HeaderLogo";
import LanguageSwitch from "./components/LanguageSwitch/LanguageSwitch";
import MobileMenu from "./components/MobileMenu/MobileMenu";
import HeaderLinks from "./components/HeaderLinks/HeaderLinks";
import StockTicker from "./components/StockTicker/StockTicker";
import { anchorWithId } from "../../helpers/anchorWithId.helper";
import Search from "./components/Search/Search";
import RoutableNotEditableLink from "../../react-only-components/RoutableNotEditableLink";

const index: FunctionComponent<HeaderProps> = ({ fields, params }) => {
  useScrollWithOffset();
  const { y } = useWindowScroll();
  const mobileBP = useMedia("(max-width: 1200px)");
  const { width } = useWindowSize();

  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    if (mobileBP || width < 1200) setIsMobile(true);
    else setIsMobile(false);

    return () => {
      setIsMobile(false);
    };
  }, [mobileBP, width]);

  useEffect(() => {
    if (mobileBP || width < 1200) setIsMobile(true);
    else setIsMobile(false);

    return () => {
      setIsMobile(false);
    };
  }, []);

  const isRoot = useMemo(() => params?.Variant === "v2", [params]);
  const getInputVal = useRef(null);

  const [isDark, setIsDark] = useState<boolean>(true);
  const [desktopDrawerState, setDesktopDrawerState] = useState<
    "open" | "closed"
  >("closed");

  useEffect(() => {
    if (desktopDrawerState === "open") return;
    setIsDark(y < 10);
  }, [y]);

  const handleMouseEnter = useCallback(() => {
    if (y > 10 || isMobile || desktopDrawerState === "open") return;
    setIsDark(false);
  }, [y, isMobile, desktopDrawerState]);
  const handleMouseLeave = useCallback(() => {
    if (y > 10 || isMobile || desktopDrawerState === "open") return;
    setIsDark(true);
  }, [y, isMobile, desktopDrawerState]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  // const [logoBlack, setLogoBlack] = useState<>
  useLockBodyScroll(isMobileMenuOpen);

  const handleMobileMenuToggle = useCallback(() => {
    setIsMobileMenuOpen((prevValue) => !prevValue);
    if (y > 10) return;
    setIsDark((prevValue) => !prevValue);
  }, [y]);

  const handleDesktopDrawerStateChange: (state: "open" | "closed") => void =
    useCallback((state) => {
      setDesktopDrawerState(state);
    }, []);

  useEffect(() => {
    if (desktopDrawerState === "open") {
      setIsDark(false);
    }
  }, [desktopDrawerState]);

  useEffect(() => {
    if (!isMobileMenuOpen || isMobile) return;
    setIsMobileMenuOpen(false);
    setIsDark(!(y > 10));
  }, [isMobile]);

  return (
    <>
      {!isRoot && (
        <div
          style={{
            width: "100%",
            height: isMobile ? 68 : 100,
          }}
        />
      )}

      <div
        className={`Header ${isDark && isRoot ? "" : "Header--light"} ${
          !isRoot ? "Header--non-root" : ""
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div>
          <HeaderLogo
            isDark={isDark && isRoot}
            logoBlack={fields?.logo}
            logoWhite={fields?.["Logo White"]}
            href={anchorWithId(
              fields?.logolink?.value?.href ?? '',
              fields?.logolink?.value?.anchor ?? ''
            )}
          />

          {isMobile === false && (
            <HeaderLinks
              isHover={!isDark && isRoot}
              primaryHeaderMenuLinks={fields?.primaryHeaderMenuLinks ?? []}
              onDrawerStateChange={handleDesktopDrawerStateChange}
            />
          )}

          {isMobile === false && (
            <div className="Header__price_feed_container">
              {fields?.StockTickerLink?.value?.href ? (
                <RoutableNotEditableLink href={fields.StockTickerLink.value.href} target={fields.StockTickerLink.value.target} className="Header__price_feed_inner_container_higher">
                  <StockTicker/>
                </RoutableNotEditableLink>
              ) : (
                <div className="Header__price_feed_inner_container_higher">
                  <StockTicker/>
                </div>
              )}
              <div className="Header__price_feed_inner_container_lower">
                <Search searchPage={fields?.["Search Page"]} hideSearch={fields?.["HideSearch"]} searchLabel={fields?.["Search Label"]}/>
                <LanguageSwitch languages={fields?.languagessite} />
              </div>
            </div>
          )}

          {isMobile && (
            <MobileMenu
              isOpen={isMobileMenuOpen}
              clickHandler={handleMobileMenuToggle}
              content={fields?.primaryHeaderMenuLinks ?? []}
              languages={fields?.languagessite}
              searchPage={fields?.["Search Page"]}
              hideSearch={fields?.["HideSearch"]}
              searchLabel={fields?.["Search Label"]}
              stockTickerLink={fields?.['StockTickerLink']}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default memo(index);
